import React from "react"
import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Link,
  ListItemText,
  Progress,
  Typography,
  UnorderedList,
  UnorderedListItem
} from "@carrotfertility/carotene-core"
import { DialogActionsBar } from "@carrotfertility/carotene-core-x"
import { FormattedMessage, useIntl } from "react-intl"
import { isMobileApp } from "#/lib/carrot-api/MobileNativeClient"
import { useButtonClickTracking } from "#/services/analytics/useButtonClickTracking"

interface LeavingCarrotStepProps {
  continueUrl: string | null
  isCreatingEligibility: boolean
  isReactivating?: boolean
  onClose: () => void
}

const BLUEBERRY_PRIVACY_NOTICE_URL = "https://www.blueberrypediatrics.com/t/privacy-policy"
const BLUEBERRY_TERMS_OF_SERVICE_URL = "https://www.blueberrypediatrics.com/t/terms-of-use"

function openLink(url: string) {
  if (isMobileApp()) {
    window.location.href = url
  } else {
    window.open(url, "_blank", "noopener,noreferrer")
  }
}

export function LeavingCarrotStep({
  continueUrl,
  isCreatingEligibility,
  isReactivating = false,
  onClose
}: LeavingCarrotStepProps): JSX.Element {
  const intl = useIntl()
  const { trackButtonClick } = useButtonClickTracking()

  const handleContinueClick = () => {
    openLink(continueUrl)
    trackButtonClick({ name: "You are leaving Carrot", href: continueUrl })
    onClose()
  }

  return (
    <>
      <DialogTitle>
        <FormattedMessage defaultMessage="You are leaving Carrot" />
      </DialogTitle>
      <DialogContent>
        <Typography>
          <FormattedMessage defaultMessage="You are about to leave Carrot and proceed to a third-party website operated by Blueberry Pediatrics." />
        </Typography>
        <Box>
          <Typography>
            <FormattedMessage defaultMessage="Please note:" />
          </Typography>
          <UnorderedList>
            <UnorderedListItem>
              <ListItemText
                primary={intl.formatMessage({
                  defaultMessage:
                    "Carrot is not responsible for the accuracy or legality of the content found on Blueberry Pediatrics' website nor for Blueberry Pediatrics' security or privacy practices."
                })}
              />
            </UnorderedListItem>
            <UnorderedListItem>
              <ListItemText
                primary={intl.formatMessage({
                  defaultMessage:
                    "Blueberry Pediatrics will be solely responsible for the collection, use, and disclosure of your personal data."
                })}
              />
            </UnorderedListItem>
            <UnorderedListItem>
              <ListItemText
                primary={intl.formatMessage(
                  {
                    defaultMessage:
                      "You can learn more about Blueberry Pediatrics' collection, use, and disclosure of personal data in their <pnLink>Privacy Notice</pnLink> and <tsLink>Terms of Service</tsLink>."
                  },
                  {
                    pnLink: (linkContent) => (
                      <Link target="_blank" color="inherit" href={BLUEBERRY_PRIVACY_NOTICE_URL}>
                        {linkContent}
                      </Link>
                    ),
                    tsLink: (linkContent) => (
                      <Link target="_blank" color="inherit" href={BLUEBERRY_TERMS_OF_SERVICE_URL}>
                        {linkContent}
                      </Link>
                    )
                  }
                )}
              />
            </UnorderedListItem>
          </UnorderedList>
        </Box>
        <Typography>
          <FormattedMessage defaultMessage="By continuing, you acknowledge and accept that Carrot is not liable or responsible for any actions or inactions taken by Blueberry Pediatrics." />
        </Typography>
      </DialogContent>
      <DialogActionsBar
        primaryAction={
          <Button
            onClick={handleContinueClick}
            color="secondary"
            variant="contained"
            disabled={isCreatingEligibility || isReactivating || !continueUrl}
          >
            {isCreatingEligibility || isReactivating ? (
              <Progress size="1.25rem" color="inherit" />
            ) : (
              <FormattedMessage defaultMessage="Continue" />
            )}
          </Button>
        }
      />
    </>
  )
}
